import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TypeaheadOption } from './types';
import { FieldValue } from '../../../types';
import { UserEvents, useUserEvent } from '../../../integrations/userEvents/useUserEvent';
import { useSmartFormContext } from '../../../forms/utils/useSmartFormContext';

export const MAX_RECENT_ITEMS = 5;

interface RecentItemsContextType {
  recentItems: TypeaheadOption<FieldValue>[];
  addRecentItem: (item: TypeaheadOption<FieldValue> | TypeaheadOption<FieldValue>[], trackRecentItem: boolean) => void;
  removeRecentItem: (itemId: string | number) => void;
}

const RecentItemsContext = createContext<RecentItemsContextType>({
  recentItems: [],
  addRecentItem: () => {},
  removeRecentItem: () => {}
});

interface RecentItemsProviderProps {
  children: React.ReactNode;
  name: string;
  recentItems?: TypeaheadOption<FieldValue>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values?: TypeaheadOption<any>[];
  isApiDriven?: boolean;
  loading?: boolean;
}

export const RecentItemsProvider: React.FC<RecentItemsProviderProps> = ({
  children,
  name,
  recentItems: filteredRecentItems,
  values,
  isApiDriven,
  loading
}) => {
  const [recentItems, setRecentItems] = useState<TypeaheadOption<FieldValue>[]>([]);
  const { featureFlags } = useSmartFormContext();
  const { track } = useUserEvent();
  const RECENT_ITEM_KEY = `recent-items-update:${name}`;

  const getStoredRecentItems = useCallback((): TypeaheadOption<FieldValue>[] => {
    const storedItems = localStorage.getItem(`recentItems:${name}`);
    if (storedItems) {
      try {
        return JSON.parse(storedItems);
      } catch {
        localStorage.removeItem(`recentItems:${name}`);
      }
    }
    return [];
  }, [name]);

  const sendRecentItemsEvent = useCallback(
    (items: TypeaheadOption<FieldValue>[]) => {
      window.dispatchEvent(new CustomEvent(RECENT_ITEM_KEY, { detail: items }));
    },
    [RECENT_ITEM_KEY]
  );

  useEffect(() => {
    if (filteredRecentItems) {
      setRecentItems(filteredRecentItems);
    } else {
      setRecentItems(getStoredRecentItems());
    }
    const syncItemChanged: EventListener = (event: Event) => {
      const detail = (event as CustomEvent<TypeaheadOption<FieldValue>[]>).detail;
      setRecentItems(detail);
    };
    window.addEventListener(RECENT_ITEM_KEY, syncItemChanged);
    return () => {
      window.removeEventListener(RECENT_ITEM_KEY, syncItemChanged);
    };
  }, [RECENT_ITEM_KEY, filteredRecentItems, getStoredRecentItems]);

  const addRecentItem = useCallback(
    (item: TypeaheadOption<FieldValue> | TypeaheadOption<FieldValue>[], trackRecentItem: boolean) => {
      const prevItems = getStoredRecentItems();
      const items = Array.isArray(item) ? item : [item];
      const filteredPrevItems = prevItems.filter(
        (prevItem: TypeaheadOption<FieldValue>) => !items.some((newItem) => newItem.id === prevItem.id)
      );

      // Store x 2 items to account for potential invalid items due to constraints.
      const updatedItems = [...items, ...filteredPrevItems].slice(0, MAX_RECENT_ITEMS * 2);
      // Omit react icon element before serializing as it contains circular references.
      const serializableItems = updatedItems.map(({ icon: _icon, ...rest }) => rest);
      localStorage.setItem(`recentItems:${name}`, JSON.stringify(serializableItems));
      sendRecentItemsEvent(updatedItems);
      setRecentItems(updatedItems);
      if (trackRecentItem) {
        track(UserEvents.menuSelectEvents.SELECT_SMART_FORM_RECENT_ITEM, { name });
      } else {
        track(UserEvents.menuSelectEvents.SELECT_SMART_FORM_MENU_ITEM, { name });
      }
    },
    [getStoredRecentItems, sendRecentItemsEvent, track, name]
  );

  const removeRecentItem = useCallback(
    (itemId: string | number) => {
      const prevItems = getStoredRecentItems();
      const updatedItems = prevItems.filter((item: TypeaheadOption<FieldValue>) => item.id !== itemId);
      const serializableItems = updatedItems.map(({ icon: _icon, ...rest }) => rest);
      localStorage.setItem(`recentItems:${name}`, JSON.stringify(serializableItems));
      sendRecentItemsEvent(updatedItems);
      setRecentItems(updatedItems);
      track(UserEvents.menuSelectEvents.REMOVE_SMART_FORM_RECENT_ITEM, { name });
    },
    [getStoredRecentItems, sendRecentItemsEvent, track, name]
  );

  const displayedRecentItems = useMemo((): TypeaheadOption<FieldValue>[] => {
    if (!featureFlags?.reconcileRecentItems || isApiDriven || loading || !values?.length) {
      return recentItems;
    }
    const byId = new Map(values.map((v) => [v.id, v]));
    return recentItems.reduce<TypeaheadOption<FieldValue>[]>((acc, item) => {
      const reconciled = byId.get(item.id);
      if (reconciled) {
        acc.push({
          ...item,
          label: reconciled.label,
          code: reconciled.code,
          subtitle: reconciled.subtitle,
          value: reconciled.value
        });
      }
      return acc;
    }, []);
  }, [featureFlags?.reconcileRecentItems, isApiDriven, loading, values, recentItems]);

  const value = useMemo(
    // Storage keeps MAX_RECENT_ITEMS * 2 as a buffer for constraint filtering; cap the
    // displayed list back to MAX_RECENT_ITEMS after invalid items have been dropped.
    () => ({ recentItems: displayedRecentItems.slice(0, MAX_RECENT_ITEMS), addRecentItem, removeRecentItem }),
    [displayedRecentItems, addRecentItem, removeRecentItem]
  );

  return <RecentItemsContext.Provider value={value}>{children}</RecentItemsContext.Provider>;
};

export const useRecentItemsContext = () => useContext<RecentItemsContextType>(RecentItemsContext);
